import { useState, useRef } from "react";
import { ArrowRightLeft, Upload, Loader2, Download, RotateCcw, FileSpreadsheet, FileJson } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const EXAMPLE_JSON = `[
  { "name": "Alice Johnson", "department": "Engineering", "role": "Senior Developer", "salary": 95000 },
  { "name": "Bob Smith", "department": "Marketing", "role": "Campaign Manager", "salary": 72000 },
  { "name": "Carol White", "department": "Engineering", "role": "Tech Lead", "salary": 110000 },
  { "name": "David Brown", "department": "Sales", "role": "Account Executive", "salary": 68000 },
  { "name": "Eve Davis", "department": "HR", "role": "People Partner", "salary": 78000 }
]`;

export default function JsonToExcel() {
  const [jsonText, setJsonText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloaded, setDownloaded] = useState(false);
  const [jsonValid, setJsonValid] = useState(null);
  const fileRef = useRef(null);

  const validateJson = (text) => {
    if (!text.trim()) { setJsonValid(null); return; }
    try {
      JSON.parse(text);
      setJsonValid(true);
      setError("");
    } catch {
      setJsonValid(false);
    }
  };

  const handleTextChange = (e) => {
    setJsonText(e.target.value);
    validateJson(e.target.value);
    setDownloaded(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setJsonText(text);
      validateJson(text);
      setDownloaded(false);
    };
    reader.readAsText(file);
  };

  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleConvert = async () => {
    if (!jsonText.trim()) return;
    setLoading(true);
    setError("");
    setDownloaded(false);
    setDownloadUrl(null);

    try {
      const res = await axios.post(`${API}/json-to-excel`, { json_text: jsonText.trim() }, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      setDownloadUrl(url);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "converted_data.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloaded(true);
    } catch (err) {
      if (err.response?.data) {
        const text = await err.response.data.text();
        try {
          setError(JSON.parse(text).detail);
        } catch {
          setError("Conversion failed. Check your JSON format.");
        }
      } else {
        setError("Conversion failed. Check your JSON format.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#ECFDF5] flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-[#10B981]" />
            </div>
            <span className="label-mono">JSON to Excel</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="json-excel-page-title">
            JSON to Excel converter
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Paste JSON data or upload a .json file and download a formatted Excel spreadsheet instantly. No AI, pure conversion.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="label-mono">JSON Data</label>
                <div className="flex items-center gap-2">
                  {jsonValid === true && <span className="text-xs text-green-600 font-medium">Valid JSON</span>}
                  {jsonValid === false && <span className="text-xs text-red-500 font-medium">Invalid JSON</span>}
                </div>
              </div>
              <Textarea
                value={jsonText}
                onChange={handleTextChange}
                placeholder='[{"name": "John", "role": "Developer"}, ...]'
                className="min-h-[300px] font-mono text-xs bg-slate-50 border-transparent focus:border-[#10B981] focus:bg-white rounded-md resize-none"
                data-testid="json-input"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleConvert}
                disabled={loading || !jsonText.trim() || jsonValid === false}
                className="bg-[#10B981] hover:bg-[#059669] text-white rounded-md px-6 h-11 font-medium btn-press"
                data-testid="convert-button"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Converting...</> : <><Download className="w-4 h-4 mr-2" />Convert & Download</>}
              </Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press" data-testid="upload-json-button">
                <Upload className="w-4 h-4 mr-1.5" />Upload .json
              </Button>
              <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileUpload} data-testid="json-file-input" />
              <Button variant="outline" onClick={() => { setJsonText(EXAMPLE_JSON); validateJson(EXAMPLE_JSON); setDownloaded(false); }} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 hover:border-[#10B981] hover:text-[#10B981] btn-press" data-testid="load-json-example">
                <FileJson className="w-4 h-4 mr-1.5" />Load Example
              </Button>
              {jsonText && (
                <Button variant="outline" onClick={() => { setJsonText(""); setJsonValid(null); setDownloaded(false); setError(""); }} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press" data-testid="json-reset-button">
                  <RotateCcw className="w-4 h-4 mr-1.5" />Clear
                </Button>
              )}
            </div>

            {error && <p className="text-sm text-red-500" data-testid="json-error">{error}</p>}
            {downloaded && (
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="json-success">
                <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Excel file ready!</p>
                  {downloadUrl && (
                    <a href={downloadUrl} download="converted_data.xlsx" target="_blank" rel="noopener noreferrer" className="text-sm text-[#10B981] hover:underline font-medium" data-testid="json-manual-download">
                      Click here if download didn't start
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="border border-slate-200 rounded-lg bg-white p-6">
              <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>Supports</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Array of objects</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Nested objects (flattened)</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Single objects</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Auto column headers</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Styled headers & filters</li>
              </ul>
            </div>
            <AdUnit format="vertical" />
          </div>
        </div>
      </div>
    </div>
  );
}
